/**
 * SchemaIndividual - A TBox-level named individual defined in the ontology.
 *
 * Used for:
 * - Enumerations (owl:oneOf members)
 * - Value constraints (owl:hasValue targets)
 * - Controlled lists (predefined allowed values)
 *
 * Implements the Individual interface from semantical-core for unified handling
 * with ABox individuals (from knowledge-graph module).
 */

import type { Individual } from "@semantical/modules/semantical-core";
import Concept from "@semantical/modules/vocabulary/Concept.ts";

export default class SchemaIndividual implements Individual {
    concept: Concept;

    /** URIs of classes this individual is an instance of (rdf:type) */
    classUris: string[];

    constructor(
        uri: string,
        label: string,
        classUris: string[] = [],
        definition: string = ''
    ) {
        this.concept = new Concept(uri, label, [], [], definition);
        this.classUris = classUris;
    }

    get uri(): string {
        return this.concept.uri;
    }

    get name(): string {
        return this.concept.prefLabel;
    }

    get definition(): string {
        return this.concept.definition;
    }

    /**
     * Check if this individual is an instance of a given class
     */
    isInstanceOf(classUri: string): boolean {
        return this.classUris.includes(classUri);
    }

    toJSON() {
        return {
            uri: this.uri,
            label: this.name,
            classUris: this.classUris,
            definition: this.definition
        };
    }

    static fromJSON(json: any): SchemaIndividual {
        return new SchemaIndividual(
            json.uri,
            json.label,
            json.classUris ?? [],
            json.definition ?? ''
        );
    }
}

export { SchemaIndividual };
